import { useRef, useState } from "react";
import { UploadCloud, Pause, Play, X, CheckCircle2, AlertTriangle, Loader2, RotateCcw } from "lucide-react";
import { api, errMsg, fmtBytes, CATEGORIES } from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const hex = (buf) => Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
const sessKey = (f, target) => `mroue_up_${f.name}_${f.size}_${f.lastModified}_${target}`;

export async function uploadFile(file, { category, replaceId, onState, control, resumeId }) {
  const target = replaceId ? `r-${replaceId}` : category;
  const key = sessKey(file, target);
  let sess = null;
  let received = new Set();
  const saved = resumeId || localStorage.getItem(key);
  if (saved) {
    try {
      const { data } = await api.get(`/admin/uploads/${saved}`);
      if (data.status === "uploading") { sess = { upload_id: saved, total_chunks: data.total_chunks }; received = new Set(data.received); }
    } catch { /* session gone, start fresh */ }
  }
  if (!sess) {
    const { data } = await api.post("/admin/uploads", { filename: file.name, size: file.size, category, replace_photo_id: replaceId || null });
    sess = data;
    localStorage.setItem(key, data.upload_id);
  } else {
    const cfg = await api.get("/admin/uploads/config");
    sess.chunk_size = cfg.data.chunk_size;
  }
  const { upload_id: uid, chunk_size: CH, total_chunks: total } = sess;
  const loaded = new Map();
  received.forEach((i) => loaded.set(i, Math.min(CH, file.size - i * CH)));
  const report = (extra = {}) => onState({ phase: "uploading", bytes: [...loaded.values()].reduce((a, b) => a + b, 0), resumed: received.size, ...extra });
  report();
  const queue = [...Array(total).keys()].filter((i) => !received.has(i));
  const worker = async () => {
    while (queue.length) {
      while (control.paused) { if (control.cancelled) return; await sleep(300); }
      if (control.cancelled) return;
      const i = queue.shift();
      const blob = file.slice(i * CH, Math.min((i + 1) * CH, file.size));
      const buf = await blob.arrayBuffer();
      const sha = window.crypto?.subtle ? hex(await window.crypto.subtle.digest("SHA-256", buf)) : undefined;
      for (let attempt = 0; ; attempt++) {
        try {
          await api.put(`/admin/uploads/${uid}/chunks/${i}`, buf, {
            headers: { "Content-Type": "application/octet-stream", ...(sha ? { "X-Chunk-SHA256": sha } : {}) },
            onUploadProgress: (e) => { loaded.set(i, e.loaded); report(); },
          });
          loaded.set(i, buf.byteLength);
          report();
          break;
        } catch (e) {
          if (attempt >= 5 || [400, 401, 403, 404].includes(e?.response?.status)) throw e;
          report({ retrying: i + 1 });
          await sleep(800 * 2 ** attempt);
        }
      }
    }
  };
  await Promise.all([worker(), worker(), worker()]);
  if (control.cancelled) { await api.delete(`/admin/uploads/${uid}`).catch(() => {}); localStorage.removeItem(key); throw new Error("Cancelled"); }
  onState({ phase: "verifying", bytes: file.size });
  const { data: done } = await api.post(`/admin/uploads/${uid}/complete`);
  localStorage.removeItem(key);
  onState({ phase: "processing", bytes: file.size, sha256: done.sha256, photo_id: done.photo_id });
  for (let k = 0; k < 600; k++) {
    await sleep(2000);
    const { data: photos } = await api.get("/admin/photos");
    const p = (Array.isArray(photos) ? photos : []).find((x) => x.id === done.photo_id);
    if (p?.status === "ready") { onState({ phase: "ready", bytes: file.size, sha256: done.sha256, photo: p }); return p; }
    if (p?.status === "failed") throw new Error(p.error || "Processing failed");
  }
  throw new Error("Processing timed out");
}

const Row = ({ item, onPause, onCancel, onRetry }) => {
  const s = item.state || {};
  const pct = Math.round(((s.bytes || 0) / item.file.size) * 100);
  const phaseLabel = { queued: "Queued", uploading: s.retrying ? `Retrying part ${s.retrying}…` : "Uploading", verifying: "Verifying integrity", processing: "Optimizing (WebP 3500 px)…", ready: "Published", error: "Failed" }[s.phase || "queued"];
  return (
    <div className="border border-white/10 bg-[#111] p-4" data-testid={`upload-item-${item.id}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <div className="truncate text-sm text-white">{item.file.name}</div>
          <div className="mt-1 font-mono text-[11px] text-white/55">{fmtBytes(item.file.size)} · {item.category}{s.resumed ? ` · resumed (${s.resumed} parts already on server)` : ""}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {s.phase === "ready" && <CheckCircle2 className="text-[#5d9a35]" size={18} />}
          {s.phase === "error" && <button onClick={onRetry} data-testid={`upload-retry-${item.id}`} className="text-white/80 hover:text-white" title="Retry / resume"><RotateCcw size={16} /></button>}
          {["processing", "verifying"].includes(s.phase) && <Loader2 className="animate-spin text-white/70" size={16} />}
          {s.phase === "uploading" && <button onClick={onPause} data-testid={`upload-pause-${item.id}`} className="text-white/80 hover:text-white">{item.control.paused ? <Play size={16} /> : <Pause size={16} />}</button>}
          {!["ready", "processing", "verifying"].includes(s.phase) && <button onClick={onCancel} data-testid={`upload-cancel-${item.id}`} className="text-white/60 hover:text-white"><X size={16} /></button>}
        </div>
      </div>
      <div className="mt-3 h-1 w-full bg-white/10"><div className={`h-full transition-[width] duration-300 ${s.phase === "error" ? "bg-red-500" : "bg-[#5d9a35]"}`} style={{ width: `${pct}%` }} /></div>
      <div className="mt-2 flex justify-between font-mono text-[11px] text-white/60">
        <span data-testid={`upload-status-${item.id}`}>{item.control.paused && s.phase === "uploading" ? "Paused" : phaseLabel}{s.error ? ` — ${s.error}` : ""}</span>
        <span>{pct}%</span>
      </div>
      {s.sha256 && <div className="mt-1 truncate font-mono text-[10px] text-white/45">SHA-256 {s.sha256}</div>}
      {s.photo && <div className="mt-1 font-mono text-[10px] text-white/55">Source {s.photo.original?.width}×{s.photo.original?.height} → display {s.photo.variants?.display?.width}×{s.photo.variants?.display?.height} · {fmtBytes((s.photo.variants?.display?.size || 0) + (s.photo.variants?.medium?.size || 0) + (s.photo.variants?.thumb?.size || 0))} stored</div>}
    </div>
  );
};

export const Uploader = ({ onDone, replaceId, config, compact }) => {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("wildlife");
  const [drag, setDrag] = useState(false);
  const inp = useRef(null);
  const update = (id, patch) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const start = async (item) => {
    update(item.id, { state: { phase: "uploading", bytes: 0 } });
    try {
      await uploadFile(item.file, { category: item.category, replaceId, control: item.control, onState: (s) => update(item.id, { state: s }) });
      onDone?.();
    } catch (e) {
      update(item.id, { state: { ...(item.state || {}), phase: "error", error: errMsg(e) } });
    }
  };
  const add = (files) => {
    const max = (config?.max_upload_mb || 500) * 1024 * 1024;
    const list = [...files].slice(0, replaceId ? 1 : 50).map((f) => ({ id: Math.random().toString(36).slice(2, 9), file: f, category, control: { paused: false, cancelled: false }, state: f.size > max ? { phase: "error", error: `Larger than ${config?.max_upload_mb} MB limit` } : { phase: "queued" } }));
    setItems((xs) => [...list, ...xs]);
    (async () => { for (const it of list) if (it.state.phase === "queued") await start(it); })();
  };
  return (
    <div data-testid="uploader">
      {!replaceId && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <span className="kicker">Category</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="upload-category-select" className="border border-white/15 bg-[#111] px-3 py-2 text-sm text-white">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}
      <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        onClick={() => inp.current?.click()} data-testid="upload-dropzone"
        className={`flex cursor-pointer flex-col items-center justify-center border border-dashed text-center transition-colors ${compact ? "p-6" : "p-12"} ${drag ? "border-[#5d9a35] bg-[#1f4209]/20" : "border-white/20 hover:border-white/50"}`}>
        <UploadCloud className="mb-3 text-white/80" size={compact ? 24 : 34} strokeWidth={1.3} />
        <div className="text-sm text-white">{replaceId ? "Drop the replacement photograph" : "Drag & drop photographs, or click to browse"}</div>
        <div className="mt-2 font-mono text-[11px] text-white/55">JPEG · PNG · WebP · TIFF{config?.heic ? " · HEIC" : ""}{config?.raw ? " · ARW/DNG" : ""} · up to {config?.max_upload_mb || 500} MB each · auto-optimized to {config?.display_max_px || 3500} px WebP</div>
        <input ref={inp} type="file" multiple={!replaceId} hidden data-testid="upload-file-input" accept=".jpg,.jpeg,.png,.webp,.tif,.tiff,.heic,.heif,.arw,.dng,.nef,.cr2,.cr3" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
      </div>
      <div className="mt-4 space-y-2">
        {items.map((it) => (
          <Row key={it.id} item={it}
            onPause={() => { it.control.paused = !it.control.paused; update(it.id, { control: it.control }); }}
            onCancel={() => { it.control.cancelled = true; it.control.paused = false; setItems((xs) => xs.filter((x) => x.id !== it.id)); }}
            onRetry={() => { it.control = { paused: false, cancelled: false }; start(it); }} />
        ))}
      </div>
    </div>
  );
};
